"use client";
import { CourseVideo } from "@/components/CourseContent/ContentOverview/types";
import {
  ElementClickedWebElement,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import React, { useState } from "react";
import { ComponentItem } from "@/utils/getSortedComponentsBySectionOrder";
import { HeroSideRailContainerProps } from "./types";
import SideRailTitle from "@/components/SideRail/HeroSideRail/SideRail/SideRailTitle";
import SideRailImage from "@/components/SideRail/HeroSideRail/SideRail/SideRailImage";
import ProgramPricing from "@/components/ProgramDetailLayout/templates/components/ProgramPricing";
import ProgramEnrollButton from "@/components/ProgramDetailLayout/templates/components/ProgramEnrollButton";
import CourseRunSelector from "@/components/CourseContent/ContentRefresh/CourseRunSelector";
import ProgramCourses from "./ProgramCourses";
import BulletListInfoSidebar from "./BulletListInfo";
import ProgramEndorsements from "@/components/ProgramDetailLayout/templates/NewDesign/components/ProgramEndorsements";
import PartnerLogo from "./PartnerLogo";
import StatisticComponent from "./StatisticComponent";
import ExpandableTextByLines from "@/components/CourseContent/Expand/ExpandableTextByLines";
import SideRailProductTitle from "@/components/SideRail/HeroSideRail/SideRail/SideRailProductTitle";
import SideRailRating from "@/components/SideRail/HeroSideRail/SideRail/SideRailRating";
import SideRailSkillsList from "@/components/SideRail/HeroSideRail/SideRail/SideRailSkillsList";
import SideRailVideoPlayer from "@/components/SideRail/HeroSideRail/SideRail/SideRailVideoPlayer";
import SideRailImageTitle from "@/components/SideRail/HeroSideRail/SideRail/SideRailImageTitle";
import { Program } from "@/common/program/types";
import { ProcessedCourse } from "@/common/course/types";
import isCourseType from "@/utils/functions/isCourseType";
import SideRailTestimonials, {
  LearnerTestimonialsProps,
} from "@/components/SideRail/HeroSideRail/SideRailTestimonials";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import CurrencyInlinePill from "@/components/CurrencySelector/CurrencyInlinePill";
import { FeatureFlagComponentResolverClient } from "@/lib/feature-flags/FeatureFlagResolverClient";
import getCoursePrice from "@/lib/course/utils/getCoursePrice";
import ProgramRetiredMessage from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage";
import {
  PROGRAM_RETIRED_MESSAGE,
  PROGRAM_UNAVAILABLE_MESSAGE,
} from "@/components/ProgramDetailLayout/templates/components/ProgramRetiredMessage/constants";
import HeroAccentCallout from "@/components/GeoPricing/HeroAccentCallout";
import ActionCardWrapper from "@/components/GeoPricing/ActionCardWrapper";
import { ProgramCurrencyInlinePill } from "@/components/ProgramDetailLayout/templates/components/ProgramCurrencySurfaces";
import { resolveCurrencySurfaces } from "@/lib/currency/resolveCurrencySurfaces";
import { getProgramPricingAmounts } from "@/lib/currency/getProgramPricingAmounts";

interface Props {
  sideRailImage?: { imageUrl?: string; altDescription?: string };
  sideRailTitle?: { text?: string | undefined };
  sideRailBulletList?: {
    title?: string;
    items?: { heading: string; description: string }[];
    theme?: "light" | "dark";
  };
  sideRailProductTitle?: { text?: string | undefined };
  sideRailSkillsList?: { skills?: string[] };
  sideRailVideoPlayer?: { title?: string; video?: CourseVideo };
  sideRailImageTitle?: { isDarkMode?: boolean; isHorizontal?: boolean };
  sideRailStatisticComponent?: {
    title?: string;
    theme?: "light" | "dark";
    items?: {
      percentage: number;
      text: string;
      source: string;
      showItem: boolean;
    }[];
  };
}

export default function useSideRailComponentList({
  productData,
  courses,
  pricingData,
  skus,
  programIsEnrollable,
  programIsRetired = false,
  sideRailImage,
  sideRailTitle,
  sideRailBulletList,
  sideRailRating,
  sideRailSkillsList,
  sideRailVideoPlayer,
  sideRailImageTitle,
  sideRailStatisticComponent,
  currencySelector,
  sideRailBodyComponents,
  isCollapsed = false,
  straightToCheckout,
}: HeroSideRailContainerProps & { isCollapsed?: boolean }) {
  const { enrollmentCount, partners } = productData;

  const parsedDescription = sanitizeAndParseInlineHTML(
    (productData as ProcessedCourse).productShortDescription || "",
  );
  const courseDescription = parsedDescription as
    | string
    | JSX.Element
    | JSX.Element[];

  const skillsList = sideRailSkillsList?.skills || productData.associatedSkills;
  const isProgram = !isCourseType(productData.productType);

  const [sideRailElement] = useState<ElementClickedWebElement>({
    elementType: ElementType.Button,
    htmlId: "earn-cert",
    location: "hero-side-rail",
    position: "1",
    text: "",
    name: "course-conversion",
  });

  const { elementClicked } = useElementEvents({
    webElement: sideRailElement,
  });

  const handleCTAEvent = (updatedText: string = "") => {
    const updatedElement = updatedText
      ? { ...sideRailElement, text: updatedText }
      : sideRailElement;

    elementClicked?.({ webElement: updatedElement });
  };
  const programOverviewVideo =
    Array.isArray(productData.video) && productData.video.length > 0
      ? (productData.video[0] as CourseVideo)
      : productData.video
        ? ((productData as ProcessedCourse).video as CourseVideo)
        : undefined;

  const componentList: ComponentItem[] = [
    {
      key: "programSkills",
      renderer: () =>
        skillsList?.length > 0 ? (
          <SideRailSkillsList skills={skillsList} />
        ) : null,
    },
    {
      key: "sideRailTitle",
      renderer: () => (
        <SideRailTitle
          key="sideRailTitle"
          partners={partners}
          partnerLogoOverride={
            productData.partnerLogoOverride
              ? productData.partnerLogoOverride
              : ""
          }
          sideRailTitle={sideRailTitle}
        />
      ),
    },
    {
      key: "courseDescription",
      renderer: () => (
        <ExpandableTextByLines
          key="expandableText"
          text={courseDescription}
          className="[&_p]:m-0"
        />
      ),
    },
    {
      key: "heroAccentCallout",
      renderer: () => (
        <HeroAccentCallout
          pricingData={pricingData}
          variant="light"
          className="mt-4"
          location={isProgram ? "program-detail-page" : "course-detail-page"}
        />
      ),
    },
    {
      key: "videoPlayer",
      renderer: () => (
        <SideRailVideoPlayer
          key="videoPlayer"
          title={
            sideRailVideoPlayer?.title || (productData as Program).title || ""
          }
          video={sideRailVideoPlayer?.video || programOverviewVideo}
          thumbnail={
            !isProgram
              ? (productData as ProcessedCourse).productImageUrl
              : undefined
          }
        />
      ),
    },
    {
      key: "image",
      renderer: () => (
        <SideRailImage key="image" sideRailImage={sideRailImage} />
      ),
    },
    {
      key: "rating",
      renderer: () => (
        <SideRailRating
          key="rating"
          count={(productData as ProcessedCourse).ratings?.count}
          average={(productData as ProcessedCourse).ratings?.average}
          enrollmentCount={enrollmentCount}
        />
      ),
    },
    {
      key: "pricing",
      renderer: () => {
        if (!isProgram) {
          if (currencySelector?.placement !== "inline-dropdown") return null;
          const courseData = productData as ProcessedCourse;
          const amounts = getProgramPricingAmounts(pricingData);
          const fallbackPrice = courseData.activeCourseRun
            ? getCoursePrice(courseData.activeCourseRun)
            : 0;
          const price = amounts?.price ?? fallbackPrice;
          if (price <= 0) return null;

          return (
            <div className="xl:hidden">
              <FeatureFlagComponentResolverClient
                flagKey="NEXT_PUBLIC_FEATURE_MULTI_CURRENCY_ACCEPTANCE"
                variants={{
                  legacy: null,
                  test: (
                    <CurrencyInlinePill
                      price={price}
                      originalPrice={amounts?.originalPrice}
                      label={currencySelector?.label}
                      mobileLabel={currencySelector?.mobileLabel}
                      tooltip={currencySelector?.tooltip}
                      location="single-course-detail-page"
                      countryAutoDetection={
                        currencySelector?.countryAutoDetection
                      }
                    />
                  ),
                }}
                fallback="legacy"
              />
            </div>
          );
        }

        if (!pricingData) return null;

        const currencySurfaces = resolveCurrencySurfaces(currencySelector);
        const isCurrencyBar = currencySelector?.placement === "currency-bar";
        const sideRailSurfaceEnabled = currencySelector?.surfaces
          ? currencySurfaces.sideRail
          : true;
        const showProgramSideRailCurrency =
          isProgram &&
          !!currencySelector &&
          sideRailSurfaceEnabled &&
          (currencySelector.surfaces?.sideRail === true ||
            !isCurrencyBar ||
            isCollapsed);
        const location = isProgram
          ? "program-pdp-side-rail"
          : "single-course-detail-page";

        return (
          <ActionCardWrapper
            key="pricing"
            pricingData={pricingData}
            location={isProgram ? "program-detail-page" : "course-detail-page"}
            className="flex w-full max-w-full flex-col items-stretch justify-center gap-2 overflow-x-clip"
          >
            {showProgramSideRailCurrency ? (
              <ProgramCurrencyInlinePill
                pricingData={pricingData}
                location={location}
                currencySelector={currencySelector}
                showPrice
                variant="default"
                layout="sideRail"
                className="w-full max-w-full"
              />
            ) : (
              <div className="flex w-full min-w-0 flex-row gap-2 font-semibold">
                <ProgramPricing
                  pricingData={pricingData}
                  stackBigPrices={false}
                />
              </div>
            )}
          </ActionCardWrapper>
        );
      },
    },
    {
      key: "ctaButton",
      renderer: () => {
        if (
          isProgram &&
          (programIsRetired ||
            !(
              "programEligibleForOneClickPurchase" in productData &&
              (productData as Program).programEligibleForOneClickPurchase
            ))
        ) {
          const message = programIsRetired
            ? PROGRAM_RETIRED_MESSAGE
            : PROGRAM_UNAVAILABLE_MESSAGE;

          return (
            <div key="ctaButton" className="w-full flex justify-center">
              <ProgramRetiredMessage message={message} />
            </div>
          );
        }

        if (
          isProgram &&
          programIsEnrollable &&
          "programEligibleForOneClickPurchase" in productData &&
          (productData as Program).programEligibleForOneClickPurchase
        ) {
          return (
            <div key="ctaButton">
              <ProgramEnrollButton
                programData={productData as Program}
                courses={courses}
                skus={skus}
                additionalClickEvent={handleCTAEvent}
              />
            </div>
          );
        }

        // COURSE CTA BTN
        if (!isProgram) {
          const courseData = productData as ProcessedCourse;

          return (
            <div key="ctaButton" className="side-rail-course-selector">
              <CourseRunSelector
                course={courseData}
                name="side-rail-cta"
                location="side-rail"
                auditTextClassName="text-sm"
                showEndDate={false}
                auditLinkLocation="inline"
                variant="box"
                isInCollapsedBody={isCollapsed}
                straightToCheckout={straightToCheckout}
                pricingData={pricingData}
              />
            </div>
          );
        }

        return null;
      },
    },
    {
      key: "programCourses",
      renderer: () =>
        Array.isArray(courses) && courses.length > 0 ? (
          <ProgramCourses key="programCourses" courses={courses} />
        ) : null,
    },
    {
      key: "bulletList",
      renderer: () => (
        <BulletListInfoSidebar
          key="bulletList"
          sideRailBulletList={sideRailBulletList}
        />
      ),
    },
    {
      key: "industryInsights",
      renderer: () => (
        <StatisticComponent
          sideRailStatisticComponent={sideRailStatisticComponent}
        />
      ),
    },
    {
      key: "endorsements",
      renderer: () =>
        (productData as Program).corporateEndorsements?.length > 0 ||
        (productData as Program).individualEndorsements?.length > 0 ? (
          <ProgramEndorsements
            key="endorsements"
            corporateEndorsements={
              (productData as Program).corporateEndorsements
            }
            individualEndorsements={
              (productData as Program).individualEndorsements
            }
            isInSideRail={true}
          />
        ) : null,
    },
    {
      key: "partnerLogo",
      renderer: () =>
        productData.partners?.length > 0 ? (
          <PartnerLogo
            key="partnerLogo"
            title={
              "title" in productData && productData.title
                ? productData.title
                : "productName" in productData && productData.productName
                  ? productData.productName
                  : ""
            }
            partners={productData.partners}
            partnerLogoOverride={productData.partnerLogoOverride}
          />
        ) : null,
    },
    {
      key: "imageTitle",
      renderer: () => (
        <SideRailImageTitle
          key="imageTitle"
          partners={productData.partners}
          sideRailImageTitle={sideRailImageTitle}
        />
      ),
    },
    {
      key: "productTitle",
      renderer: () => {
        let title = "";

        // Generate titles for courses
        if ("productName" in productData && productData.productName) {
          const partnerNames = productData.partners
            .map((obj) => obj.key)
            .join(", ");
          const courseName = (productData as ProcessedCourse).productName;
          title = `${partnerNames}: ${courseName}`.replace(/\.$/, ""); // Remove trailing period
        }
        // Generate titles for programs
        else if ("title" in productData && productData.title) {
          title = productData.title;
        }

        return <SideRailProductTitle key="productTitle" title={title} />;
      },
    },
    {
      key: "testimonials",
      renderer: () => {
        const testimonials = (productData as ProcessedCourse)
          .learnerTestimonials as LearnerTestimonialsProps["testimonials"];

        if (!testimonials) return null;

        return (
          <SideRailTestimonials
            key="learnerTestimonials"
            testimonials={testimonials}
          />
        );
      },
    },
    //Add new components here
  ];

  return componentList;
}
